import {
  Bot,
  ChevronDown,
  Database,
  File,
  FileText,
  ImagePlus,
  Info,
  Send,
  Server,
  SlidersHorizontal,
  Square,
  Wrench,
  X,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  ConversationImageAttachment,
  DEFAULT_SEARCH_CONTEXT_TYPE,
  MessageInputProps,
} from '../../types/conversation';
import anthropicLogo from '../../assets/llm-providers/anthropic.png';
import geminiLogo from '../../assets/llm-providers/gemini.png';
import openAiLogo from '../../assets/llm-providers/openai.png';
import { ConnectorLogo, getProviderLabel } from '../connectors';
import { Button, Tooltip } from '../ui';

const resolveProviderKey = (
  provider: string | undefined,
  modelId: string | undefined
): 'openai' | 'anthropic' | 'google' | 'default' => {
  const raw = (provider || modelId?.split('/')[0] || '').toLowerCase();
  if (raw === 'openai') {
    return 'openai';
  }
  if (raw === 'anthropic') {
    return 'anthropic';
  }
  if (raw === 'google' || raw === 'gemini') {
    return 'google';
  }
  return 'default';
};

const ProviderBadge: React.FC<{
  provider: string | undefined;
  modelId: string | undefined;
}> = ({ provider, modelId }) => {
  const providerKey = resolveProviderKey(provider, modelId);
  const providerLabel =
    providerKey === 'openai'
      ? 'OpenAI'
      : providerKey === 'anthropic'
        ? 'Anthropic'
        : providerKey === 'google'
          ? 'Google Gemini'
          : 'LLM';
  const providerLogoByKey: Record<'openai' | 'anthropic' | 'google', string> = {
    openai: openAiLogo,
    anthropic: anthropicLogo,
    google: geminiLogo,
  };
  const providerLogo =
    providerKey === 'default' ? null : providerLogoByKey[providerKey];

  if (providerLogo) {
    return (
      <img
        src={providerLogo}
        alt={`${providerLabel} logo`}
        title={providerLabel}
        className="h-5 w-5 shrink-0 rounded-sm object-contain"
      />
    );
  }

  return (
    <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-[10px] font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-300">
      L
    </span>
  );
};

export const MessageInput: React.FC<MessageInputProps> = ({
  message,
  setMessage,
  onSendMessage,
  onStopMessage,
  isStreaming,
  disabled = false,
  useCanvas,
  onUseCanvasChange,
  searchContextType = DEFAULT_SEARCH_CONTEXT_TYPE,
  useLiveData = false,
  onUseLiveDataChange,
  selectedKnowledgeSources = [],
  selectedConnectorSources = [],
  onOpenKnowledgeBaseModal,
  selectedLlmModel = null,
  llmModels = [],
  llmDefaultModel = null,
  llmLoading = false,
  llmLoadError = false,
  onSelectedLlmModelChange,
  textareaHeightPx,
}) => {
  const MAX_IMAGE_ATTACHMENTS = 8;
  const MAX_IMAGE_SIZE_BYTES = 100 * 1024 * 1024;
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const responseMenuRef = useRef<HTMLDivElement>(null);
  const llmMenuRef = useRef<HTMLDivElement>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isResponseMenuOpen, setIsResponseMenuOpen] = useState(false);
  const [isLlmMenuOpen, setIsLlmMenuOpen] = useState(false);
  const [imageAttachments, setImageAttachments] = useState<ConversationImageAttachment[]>(
    []
  );
  const controlsDisabled = disabled || isStreaming;
  const selectedModelId = selectedLlmModel ?? llmDefaultModel ?? '';
  const selectedModel = llmModels.find(model => model.model_id === selectedModelId) || null;

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled) {
      e.preventDefault();
      const hasPayload = Boolean(message.trim()) || imageAttachments.length > 0;
      if (!hasPayload) {
        return;
      }
      onSendMessage(imageAttachments);
      setImageAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Auto-resize textarea até 4 linhas
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (typeof textareaHeightPx === 'number') {
      textarea.style.height = `${Math.max(40, textareaHeightPx)}px`;
      textarea.style.overflowY = 'auto';
      return;
    }

    // Reset height to calculate scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate max height for 4 lines (line-height: 1.5rem = 24px)
    const lineHeight = 24; // 1.5rem
    const maxHeight = lineHeight * 4; // 4 linhas
    const padding = 16; // py-2 = 8px top + 8px bottom
    
    // Set height based on content, but limit to maxHeight
    if (textarea.scrollHeight <= maxHeight + padding) {
      textarea.style.height = `${textarea.scrollHeight}px`;
      textarea.style.overflowY = 'hidden';
    } else {
      textarea.style.height = `${maxHeight + padding}px`;
      textarea.style.overflowY = 'auto';
    }
  }, [message, textareaHeightPx]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideAnyMenu =
        settingsMenuRef.current?.contains(target) ||
        responseMenuRef.current?.contains(target) ||
        llmMenuRef.current?.contains(target);

      if (isInsideAnyMenu) {
        return;
      }

      setIsSettingsOpen(false);
      setIsResponseMenuOpen(false);
      setIsLlmMenuOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const handlePickImages = () => {
    fileInputRef.current?.click();
  };

  const appendImageFiles = (files: File[]) => {
    if (!files.length) {
      return;
    }

    const availableSlots = Math.max(0, MAX_IMAGE_ATTACHMENTS - imageAttachments.length);
    if (availableSlots === 0) {
      return;
    }

    const oversized = files.filter(file => file.size > MAX_IMAGE_SIZE_BYTES);
    if (oversized.length > 0) {
      const maxMb = Math.floor(MAX_IMAGE_SIZE_BYTES / (1024 * 1024));
      const names = oversized.map(file => file.name).join(', ');
      toast.error(`Imagem excede o limite de ${maxMb}MB: ${names}`);
    }

    const accepted = files
      .filter(file => file.type.toLowerCase().startsWith('image/'))
      .filter(file => file.size <= MAX_IMAGE_SIZE_BYTES)
      .slice(0, availableSlots);

    const nextItems: ConversationImageAttachment[] = accepted.map(file => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file_name: file.name?.trim() || `imagem-colada-${Date.now()}.${file.type.split('/')[1] || 'png'}`,
      mime_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
      preview_url: URL.createObjectURL(file),
      file,
    }));

    if (nextItems.length > 0) {
      setImageAttachments(previous => [...previous, ...nextItems]);
    }
  };

  const handleImagesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    appendImageFiles(files);
    event.target.value = '';
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardItems = Array.from(event.clipboardData?.items || []);
    if (!clipboardItems.length) {
      return;
    }

    const imageFiles = clipboardItems
      .filter(item => item.type.toLowerCase().startsWith('image/'))
      .map(item => item.getAsFile())
      .filter((file): file is File => Boolean(file));

    if (!imageFiles.length) {
      return;
    }

    event.preventDefault();
    appendImageFiles(imageFiles);
  };

  const handleRemoveImage = (attachmentId: string) => {
    setImageAttachments(previous => {
      const target = previous.find(item => item.id === attachmentId);
      if (target) {
        try {
          URL.revokeObjectURL(target.preview_url);
        } catch {
          // Ignore URL cleanup errors.
        }
      }
      return previous.filter(item => item.id !== attachmentId);
    });
  };

  const selectedResponseLabel = useCanvas
    ? t('conversations.advancedResponse')
    : t('conversations.simpleResponse');

  const baseContextLabel =
    searchContextType === 'specific'
      ? t('conversations.searchContextSpecific')
      : searchContextType === 'web'
        ? t('conversations.searchContextWeb')
        : searchContextType === 'connectors'
          ? t('conversations.searchContextConnectors', 'Conectores')
          : t('conversations.searchContextGlobal');

  const contextBadge =
    searchContextType === 'specific' && selectedKnowledgeSources.length > 0
      ? `${selectedKnowledgeSources.length} doc${selectedKnowledgeSources.length > 1 ? 's' : ''}`
      : searchContextType === 'connectors' && selectedConnectorSources.length > 0
        ? `${selectedConnectorSources.length} conector${selectedConnectorSources.length > 1 ? 'es' : ''}`
        : null;

  const selectedKnowledgeLabel = contextBadge
    ? `${baseContextLabel} • ${contextBadge}`
    : baseContextLabel;

  if (disabled) {
    return (
      <div className="flex items-center justify-center p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
        <div className="flex items-center space-x-2 text-yellow-700 dark:text-yellow-300">
          <Bot className="h-5 w-5 text-yellow-700 dark:text-yellow-300" />
          <span className="text-sm font-medium">
            {t('conversations.selectTeamAbove')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-3">
      <div className="flex items-center gap-3 flex-wrap overflow-visible">
        <div ref={settingsMenuRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => {
              setIsSettingsOpen(prev => !prev);
              setIsResponseMenuOpen(false);
              setIsLlmMenuOpen(false);
            }}
            className="inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-400 hover:bg-light-50 dark:hover:bg-graphite-700 cursor-pointer w-fit"
            aria-expanded={isSettingsOpen}
            aria-label={t('conversations.advancedSettings')}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {t('conversations.advancedSettings')}
          </button>

          {isSettingsOpen && (
            <div className="absolute left-0 bottom-full mb-2 z-40 w-[min(32rem,90vw)] space-y-3 rounded-lg border border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-800 p-3 shadow-lg">
              <div className="pb-3 border-b border-light-200 dark:border-graphite-700">
                <p className="text-sm font-semibold text-deepTechBlue-500 dark:text-light-200">
                  {t('conversations.advancedSettings')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Ajuste modelo LLM e dados em tempo real.
                </p>
              </div>

              <div className="space-y-3">
                <div className="space-y-3">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-300 inline-flex items-center gap-1">
                    <Bot className="h-3.5 w-3.5" />
                    {t('conversations.llmModel')}
                  </label>
                  <div ref={llmMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setIsLlmMenuOpen(prev => !prev)}
                      disabled={controlsDisabled || llmLoading || llmModels.length === 0}
                      className="w-full inline-flex items-center justify-between gap-2 rounded-lg border border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-900 px-3 py-2 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="min-w-0 inline-flex items-center gap-2">
                        {selectedModel ? (
                          <ProviderBadge
                            provider={selectedModel.provider}
                            modelId={selectedModel.model_id}
                          />
                        ) : (
                          <Bot className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        )}
                        <span className="truncate">
                          {llmLoading
                            ? t('conversations.loadingLlmModels')
                            : selectedModel?.model_id || t('conversations.defaultLlmModel')}
                        </span>
                      </span>
                      <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    </button>

                    {isLlmMenuOpen && (
                      <div className="absolute left-0 bottom-full mb-2 z-50 w-full max-h-64 overflow-y-auto overscroll-contain rounded-lg border border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-800 shadow-lg">
                        <button
                          type="button"
                          onClick={() => {
                            onSelectedLlmModelChange?.(null);
                            setIsLlmMenuOpen(false);
                          }}
                          className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-light-50 dark:hover:bg-graphite-700"
                        >
                          <Bot className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          {t('conversations.defaultLlmModel')}
                        </button>
                        {llmModels.map(model => (
                          <button
                            key={model.model_id}
                            type="button"
                            onClick={() => {
                              onSelectedLlmModelChange?.(model.model_id || null);
                              setIsLlmMenuOpen(false);
                            }}
                            className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-light-50 dark:hover:bg-graphite-700"
                          >
                            <ProviderBadge provider={model.provider} modelId={model.model_id} />
                            <span className="truncate">{model.model_id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {llmLoadError && (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      {t('conversations.errorLoadingLlmModels')}
                    </p>
                  )}
                </div>

                <label className="flex items-center justify-between gap-3 text-sm text-gray-700 dark:text-gray-300 rounded-md border border-light-200 dark:border-graphite-700 px-3 py-2.5">
                  <span>{t('conversations.liveData')}</span>
                  <input
                    type="checkbox"
                    checked={useLiveData}
                    onChange={e => onUseLiveDataChange?.(e.target.checked)}
                    disabled={controlsDisabled}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        <div ref={responseMenuRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => {
              setIsResponseMenuOpen(prev => !prev);
              setIsSettingsOpen(false);
              setIsLlmMenuOpen(false);
            }}
            className="inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-400 hover:bg-light-50 dark:hover:bg-graphite-700 cursor-pointer w-fit"
            aria-expanded={isResponseMenuOpen}
            aria-label={t('conversations.responseType')}
          >
            <Wrench className="h-3.5 w-3.5" />
            {selectedResponseLabel}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>

          {isResponseMenuOpen && (
            <div className="absolute left-0 bottom-full mb-2 z-40 min-w-[18rem] rounded-2xl border border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-800 p-2 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  onUseCanvasChange?.(false);
                  setIsResponseMenuOpen(false);
                }}
                disabled={controlsDisabled}
                className={`w-full px-3 py-2 text-sm rounded-xl inline-flex items-center justify-between gap-2 transition-colors ${
                  !useCanvas
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-deepTechBlue-500 dark:text-light-400 hover:bg-light-50 dark:hover:bg-graphite-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span className="inline-flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  {t('conversations.simpleResponse')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  onUseCanvasChange?.(true);
                  setIsResponseMenuOpen(false);
                }}
                disabled={controlsDisabled}
                className={`w-full px-3 py-2 text-sm rounded-xl inline-flex items-center justify-between gap-2 leading-5 transition-colors ${
                  useCanvas
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-deepTechBlue-500 dark:text-light-400 hover:bg-light-50 dark:hover:bg-graphite-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span className="inline-flex items-center gap-2 min-h-5">
                  <FileText className="h-4 w-4" />
                  {t('conversations.advancedResponse')}
                </span>
                <Tooltip
                  content={t('conversations.advancedResponseTooltip')}
                  position="right"
                >
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
                    <Info className="h-4 w-4" />
                  </span>
                </Tooltip>
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => onOpenKnowledgeBaseModal?.()}
          disabled={controlsDisabled}
          className="inline-flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-400 hover:bg-light-50 dark:hover:bg-graphite-700 cursor-pointer w-fit shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('conversations.chooseKnowledgeBase')}
        >
          <Database className="h-3.5 w-3.5" />
          {selectedKnowledgeLabel}
        </button>

        {searchContextType === 'specific' && selectedKnowledgeSources.length > 0 && (
          <div className="inline-flex items-center gap-1.5 shrink-0 text-xs self-center">
            <File className="h-3.5 w-3.5 text-deepTechBlue-500 dark:text-light-400 shrink-0" />
            {selectedKnowledgeSources.map(source => (
              <span
                key={source.id}
                className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
              >
                {source.name}
              </span>
            ))}
          </div>
        )}
        {searchContextType === 'connectors' && selectedConnectorSources.length > 0 && (
          <div className="inline-flex items-center gap-1.5 shrink-0 text-xs self-center">
            <Server className="h-3.5 w-3.5 text-deepTechBlue-500 dark:text-light-400 shrink-0" />
            {selectedConnectorSources.map(source => (
              <span
                key={source.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                title={getProviderLabel(source.provider)}
              >
                <ConnectorLogo provider={source.provider} className="h-3 w-3" />
                {source.display_name}
              </span>
            ))}
          </div>
        )}
        </div>

      <div className="flex gap-3 items-end">
        <div className="flex-1 relative">
          {imageAttachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {imageAttachments.map(attachment => (
                <div
                  key={attachment.id}
                  className="relative h-16 w-16 overflow-hidden rounded-md border border-light-200 dark:border-graphite-700"
                >
                  <img
                    src={attachment.preview_url}
                    alt={attachment.file_name}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(attachment.id)}
                    className="absolute right-0.5 top-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white"
                    aria-label={t('conversations.removeImage')}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            onPaste={handlePaste}
            placeholder={t('conversations.typeMessage')}
            disabled={isStreaming}
            rows={1}
            className="input block w-full resize-none py-2 px-3 min-h-[2.5rem]"
            style={
              typeof textareaHeightPx === 'number'
                ? {
                    lineHeight: '1.5rem',
                    height: `${Math.max(40, textareaHeightPx)}px`,
                  }
                : {
                    lineHeight: '1.5rem',
                  }
            }
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleImagesSelected}
          />
        </div>
        <Button
          onClick={handlePickImages}
          disabled={isStreaming || imageAttachments.length >= MAX_IMAGE_ATTACHMENTS}
          variant="outline"
          title={t('conversations.attachImages')}
        >
          <ImagePlus className="h-4 w-4" />
        </Button>
        {isStreaming ? (
          <Button onClick={onStopMessage} disabled={!onStopMessage}>
            <Square className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={() => {
              const hasPayload = Boolean(message.trim()) || imageAttachments.length > 0;
              if (!hasPayload) {
                return;
              }
              onSendMessage(imageAttachments);
              setImageAttachments([]);
              if (fileInputRef.current) {
                fileInputRef.current.value = '';
              }
            }}
            disabled={!message.trim() && imageAttachments.length === 0}
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
